import React, { useEffect, useState, useCallback } from "react";
import NavBar from "../components/layout/NavBar";
import { HeaderBar } from "../components/layout/TopBar";
import { useAuth } from "../components/auth/AuthContext";
import { useIsAdmin } from "../hooks/useFeatureFlags";
import {
  getOrganizationUsers,
  getUserInfoById,
  setOrgAdmin,
  setUserActive,
  createOrganizationUser,
  getM365DirectoryUsers,
  activateM365Users,
  getM365Configuration,
  saveM365Configuration,
  getWindowsAuthStatus,
  getWindowsAuthConfiguration,
  saveWindowsAuthConfiguration,
} from "../api/orgAdminApi";
import { getDatabases } from "../api/adminApi";
import { getGroups, saveGroup, deleteGroup, setGroupDatabases } from "../api/groupRightsApi";
import { Hero } from "../components/adminSurface/AdminSurface";
import { UsersArt } from "../components/adminSurface/heroArt";

const AD_ROLES = ["DIXI_ADMIN", "Admin", "OrgAdmin", "User", "ReadOnly"];

function adRoleColor(role) {
  switch (role) {
    case "DIXI_ADMIN": return "#dc2626";
    case "Admin":      return "#ea580c";
    case "OrgAdmin":   return "#2563eb";
    case "User":       return "#16a34a";
    case "ReadOnly":   return "#6b7280";
    default:           return "#8b5cf6";
  }
}

function Toggle({ value, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      style={{
        width: 44, height: 24, borderRadius: 12, border: "none",
        background: value ? "#2563eb" : "#d1d5db",
        cursor: "pointer", position: "relative", flexShrink: 0,
        transition: "background 0.2s",
      }}
    >
      <span style={{
        display: "block", width: 18, height: 18, borderRadius: "50%",
        background: "#fff", position: "absolute", top: 3,
        left: value ? 23 : 3, transition: "left 0.2s",
        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
      }} />
    </button>
  );
}

function PermBadge({ label, on }) {
  return (
    <span style={{
      display: "inline-block",
      padding: "1px 7px",
      borderRadius: 10,
      fontSize: 11,
      fontWeight: 600,
      marginRight: 3,
      background: on ? "#dbeafe" : "#f3f4f6",
      color: on ? "#1d4ed8" : "#9ca3af",
      border: `1px solid ${on ? "#bfdbfe" : "#e5e7eb"}`,
    }}>
      {label}
    </span>
  );
}

const EMPTY_GROUP = {
  id: null,
  name: "",
  description: "",
  canAdminFunctions: false,
  queryLimit: "",
  canView: true,
  canDownload: false,
  canCreate: false,
  canUpdate: false,
  isActive: true,
};

// ─────────────────────────────────────────────────────────────────────────────
// Groups Tab
// ─────────────────────────────────────────────────────────────────────────────
function GroupsTab({ token, isAdmin }) {
  const [groups, setGroups] = useState([]);
  const [databases, setDatabases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  // Save Group modal
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [form, setForm] = useState(EMPTY_GROUP);

  // Manage Databases modal
  const [showDbModal, setShowDbModal] = useState(false);
  const [dbTarget, setDbTarget] = useState(null); // { id, name }
  const [dbSelected, setDbSelected] = useState({}); // { dbInfoId: true }
  const [savingDbs, setSavingDbs] = useState(false);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    if (!token || !isAdmin) return;
    setLoading(true);
    setError("");
    try {
      const [g, d] = await Promise.all([getGroups(token), getDatabases(token)]);
      setGroups(Array.isArray(g) ? g : []);
      setDatabases(Array.isArray(d) ? d : []);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [token, isAdmin]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setForm(EMPTY_GROUP);
    setError("");
    setShowSaveModal(true);
  };

  const openEdit = (g) => {
    setForm({
      id: g.id,
      name: g.name,
      description: g.description || "",
      canAdminFunctions: g.canAdminFunctions,
      queryLimit: g.queryLimit != null ? String(g.queryLimit) : "",
      canView: g.canView,
      canDownload: g.canDownload,
      canCreate: g.canCreate,
      canUpdate: g.canUpdate,
      isActive: g.isActive,
    });
    setError("");
    setShowSaveModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError("Group name is required."); return; }
    setSaving(true);
    setError("");
    try {
      await saveGroup(token, {
        id: form.id || 0,
        name: form.name.trim(),
        description: form.description.trim() || null,
        canAdminFunctions: form.canAdminFunctions,
        queryLimit: form.queryLimit !== "" ? parseInt(form.queryLimit, 10) || null : null,
        canView: form.canView,
        canDownload: form.canDownload,
        canCreate: form.canCreate,
        canUpdate: form.canUpdate,
        isActive: form.isActive,
      });
      setShowSaveModal(false);
      await load();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  const openManageDbs = (g) => {
    const sel = {};
    (g.accessibleDatabases || []).forEach((d) => { sel[d.dbInfoId] = true; });
    setDbSelected(sel);
    setDbTarget({ id: g.id, name: g.name });
    setShowDbModal(true);
  };

  const handleSaveDbs = async () => {
    setSavingDbs(true);
    try {
      const ids = Object.keys(dbSelected).filter((k) => dbSelected[k]).map(Number);
      await setGroupDatabases(token, dbTarget.id, ids);
      setShowDbModal(false);
      await load();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSavingDbs(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteGroup(token, deleteTarget.id);
      setDeleteTarget(null);
      await load();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setDeleting(false);
    }
  };

  const f = (field) => (val) => setForm((p) => ({ ...p, [field]: val }));

  return (
    <>
      {/* Header row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <p style={{ color: "#6b7280", fontSize: 14, margin: 0 }}>
          Define groups with specific permissions and database access for your organization.
        </p>
        {isAdmin && (
          <button type="button" className="chip-btn" onClick={openAdd}>
            Add group
          </button>
        )}
      </div>

      {error && <div className="org-users-status error">{error}</div>}
      {loading && <div className="org-users-status">Loading groups…</div>}
      {!loading && !error && groups.length === 0 && (
        <div className="org-users-status">No groups found. Create your first group to get started.</div>
      )}

      {groups.length > 0 && (
        <table className="org-users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th>Permissions</th>
              <th>Query limit</th>
              <th>Databases</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <tr key={g.id}>
                <td style={{ fontWeight: 600 }}>{g.name}</td>
                <td style={{ color: "#6b7280", fontSize: 13 }}>{g.description || "—"}</td>
                <td>
                  {g.canAdminFunctions && <PermBadge label="Admin" on />}
                  <PermBadge label="View" on={g.canView} />
                  <PermBadge label="Download" on={g.canDownload} />
                  <PermBadge label="Create" on={g.canCreate} />
                  <PermBadge label="Update" on={g.canUpdate} />
                </td>
                <td style={{ fontSize: 13 }}>
                  {g.queryLimit != null ? g.queryLimit : <span style={{ color: "#9ca3af" }}>Unlimited</span>}
                </td>
                <td>
                  <span
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 4,
                      background: "#f0f9ff", border: "1px solid #bae6fd",
                      borderRadius: 12, padding: "2px 10px", fontSize: 12, fontWeight: 600,
                      color: "#0369a1", cursor: "pointer",
                    }}
                    onClick={() => openManageDbs(g)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && openManageDbs(g)}
                  >
                    {(g.accessibleDatabases || []).length} DB
                  </span>
                </td>
                <td>
                  <span style={{
                    background: g.isActive ? "#f0fdf4" : "#fef2f2",
                    color: g.isActive ? "#16a34a" : "#dc2626",
                    border: `1px solid ${g.isActive ? "#bbf7d0" : "#fecaca"}`,
                    borderRadius: 10, padding: "2px 8px", fontSize: 11, fontWeight: 600,
                  }}>
                    {g.isActive ? "Active" : "Inactive"}
                  </span>
                </td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <button type="button" className="org-users-action" onClick={() => openEdit(g)}>
                    Edit
                  </button>
                  <button type="button" className="org-users-action" onClick={() => openManageDbs(g)}>
                    Databases
                  </button>
                  <button
                    type="button"
                    className="org-users-action"
                    style={{ color: "#dc2626" }}
                    onClick={() => setDeleteTarget(g)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* ── Save Group Modal ── */}
      {showSaveModal && (
        <div className="modal-overlay" onClick={() => { if (!saving) setShowSaveModal(false); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560, width: "95vw" }}>
            <div className="modal-title">{form.id ? "Edit group" : "Add group"}</div>
            <div className="modal-body" style={{ maxHeight: "75vh", overflowY: "auto" }}>

              <div className="form-row">
                <label>Group name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => f("name")(e.target.value)}
                  placeholder="e.g. Document Viewers"
                />
              </div>
              <div className="form-row">
                <label>Description</label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => f("description")(e.target.value)}
                  placeholder="Optional description"
                />
              </div>
              <div className="form-row">
                <label>Query limit</label>
                <input
                  type="number"
                  min={1}
                  value={form.queryLimit}
                  onChange={(e) => f("queryLimit")(e.target.value)}
                  placeholder="Leave blank for unlimited"
                  style={{ width: 160 }}
                />
              </div>

              {/* Permission toggles */}
              <div style={{ marginTop: 20, marginBottom: 8, fontSize: 13, fontWeight: 700, color: "#374151" }}>
                Permissions
              </div>

              {[
                { field: "canAdminFunctions", label: "Admin functions", desc: "Manage databases, workers, schemas and system settings" },
                { field: "canView",           label: "View documents",  desc: "Open and read document content" },
                { field: "canDownload",       label: "Download",        desc: "Download original files to local storage" },
                { field: "canCreate",         label: "Create / Upload", desc: "Upload new documents and create records" },
                { field: "canUpdate",         label: "Update",          desc: "Modify metadata and replace document content" },
              ].map(({ field, label, desc }) => (
                <div
                  key={field}
                  style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "10px 0", borderBottom: "1px solid #f3f4f6",
                  }}
                >
                  <div>
                    <strong style={{ fontSize: 14 }}>{label}</strong>
                    <p style={{ color: "#6b7280", fontSize: 12, margin: "2px 0 0" }}>{desc}</p>
                  </div>
                  <Toggle value={form[field]} onChange={f(field)} />
                </div>
              ))}

              <div
                style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "10px 0",
                }}
              >
                <div>
                  <strong style={{ fontSize: 14 }}>Active</strong>
                  <p style={{ color: "#6b7280", fontSize: 12, margin: "2px 0 0" }}>Enable or disable this group</p>
                </div>
                <Toggle value={form.isActive} onChange={f("isActive")} />
              </div>

              {error && <div className="error-text" role="alert" style={{ marginTop: 8 }}>{error}</div>}
            </div>
            <div className="modal-actions">
              <button type="button" className="link-btn" disabled={saving} onClick={() => { if (!saving) setShowSaveModal(false); }}>
                Cancel
              </button>
              <button type="button" className="chip-btn" disabled={saving} onClick={handleSave}>
                {saving ? "Saving…" : "Save group"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Manage Databases Modal ── */}
      {showDbModal && dbTarget && (
        <div className="modal-overlay" onClick={() => { if (!savingDbs) setShowDbModal(false); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520, width: "95vw" }}>
            <div className="modal-title">Databases — {dbTarget.name}</div>
            <div className="modal-body">
              <p style={{ color: "#6b7280", fontSize: 13, marginBottom: 12 }}>
                Select which databases members of this group can access.
              </p>
              {databases.length === 0 && (
                <div className="org-users-status">No databases available.</div>
              )}
              <div style={{ maxHeight: 360, overflowY: "auto" }}>
                {databases.map((db) => {
                  const id = db.id || db.Id;
                  const name = db.name || db.Name || db.dbName || db.DbName || String(id);
                  const display = db.displayName || db.DisplayName || db.dbDisplayName || db.DbDisplayName || name;
                  const org = db.organization || db.Organization || db.dbOrganization || db.DbOrganization || "";
                  const checked = !!dbSelected[id];
                  return (
                    <label
                      key={id}
                      style={{
                        display: "flex", alignItems: "center", gap: 10,
                        padding: "8px 4px", cursor: "pointer",
                        borderBottom: "1px solid #f9fafb",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) =>
                          setDbSelected((prev) => ({ ...prev, [id]: e.target.checked }))
                        }
                      />
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{display}</div>
                        {org && <div style={{ fontSize: 11, color: "#9ca3af" }}>{org}</div>}
                      </div>
                    </label>
                  );
                })}
              </div>
              {error && <div className="error-text" role="alert" style={{ marginTop: 8 }}>{error}</div>}
            </div>
            <div className="modal-actions">
              <button type="button" className="link-btn" disabled={savingDbs} onClick={() => { if (!savingDbs) setShowDbModal(false); }}>
                Cancel
              </button>
              <button type="button" className="chip-btn" disabled={savingDbs} onClick={handleSaveDbs}>
                {savingDbs ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm Modal ── */}
      {deleteTarget && (
        <div className="modal-overlay" onClick={() => { if (!deleting) setDeleteTarget(null); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="modal-title">Delete group</div>
            <div className="modal-body">
              <p style={{ fontSize: 14, color: "#374151" }}>
                Are you sure you want to delete <strong>{deleteTarget.name}</strong>?
                This will also remove all database access mappings for this group.
              </p>
              {error && <div className="error-text" role="alert">{error}</div>}
            </div>
            <div className="modal-actions">
              <button type="button" className="link-btn" disabled={deleting} onClick={() => { if (!deleting) setDeleteTarget(null); }}>
                Cancel
              </button>
              <button
                type="button"
                className="chip-btn"
                disabled={deleting}
                onClick={handleDelete}
                style={{ background: "#dc2626" }}
              >
                {deleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Users Tab  (lifted from OrganizationUsers.jsx)
// ─────────────────────────────────────────────────────────────────────────────
function UsersTab({ token, isAdmin, userOrg }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newOrgAdmin, setNewOrgAdmin] = useState(false);
  const [newOrg, setNewOrg] = useState(userOrg);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showM365Modal, setShowM365Modal] = useState(false);
  const [m365Loading, setM365Loading] = useState(false);
  const [m365Users, setM365Users] = useState([]);
  const [m365Selected, setM365Selected] = useState({});
  const [activatingM365, setActivatingM365] = useState(false);
  const [m365Config, setM365Config] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configForm, setConfigForm] = useState({ tenantId: "", clientId: "", clientSecret: "" });
  const [savingConfig, setSavingConfig] = useState(false);
  const [adStatus, setAdStatus] = useState(null);
  const [showAdModal, setShowAdModal] = useState(false);
  const [adConfig, setAdConfig] = useState({
    enabled: false, requireGroupMembership: true, autoProvisionUsers: true,
    syncRolesFromAd: true, defaultRole: "User", defaultOrganization: "",
    defaultEmailDomain: "", adGroupMappings: {},
  });
  const [savingAdConfig, setSavingAdConfig] = useState(false);
  const [adConfigError, setAdConfigError] = useState("");
  const [adConfigSuccess, setAdConfigSuccess] = useState("");
  const [newAdGroup, setNewAdGroup] = useState("");
  const [newAdRole, setNewAdRole] = useState("User");

  useEffect(() => { setNewOrg(userOrg); }, [userOrg]);

  useEffect(() => {
    if (!token || !isAdmin) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const data = await getOrganizationUsers(token);
        if (!cancelled) setItems(Array.isArray(data) ? data : []);
        try {
          const config = await getM365Configuration(token);
          if (!cancelled && config) setM365Config(config);
        } catch (e) { if (e.status !== 404) console.warn("M365 config:", e); }
        try {
          const status = await getWindowsAuthStatus(token);
          if (!cancelled && status) setAdStatus(status);
        } catch (e) { console.warn("AD status:", e); }
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, isAdmin]);

  const openM365Modal = async () => {
    if (!m365Config || !m365Config.isConfigured) {
      setShowConfigModal(true);
      return;
    }
    setShowM365Modal(true);
    setError("");
    setM365Loading(true);
    try {
      const data = await getM365DirectoryUsers(token);
      setM365Users(Array.isArray(data) ? data : []);
      setM365Selected({});
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setM365Loading(false);
    }
  };

  const toggleM365Selection = (userItem, checked) => {
    const email = (userItem && (userItem.email || userItem.upn)) || "";
    if (!email) return;
    setM365Selected((prev) => {
      const next = { ...prev };
      if (checked) next[email] = { email, displayName: userItem.displayName || "" };
      else delete next[email];
      return next;
    });
  };

  const handleActivateFromM365 = async () => {
    const users = Object.values(m365Selected || {});
    if (!users.length) return;
    setActivatingM365(true);
    setError("");
    try {
      await activateM365Users(token, users);
      const data = await getOrganizationUsers(token);
      setItems(Array.isArray(data) ? data : []);
      setShowM365Modal(false);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setActivatingM365(false);
    }
  };

  const handleSaveM365Config = async () => {
    const { tenantId, clientId, clientSecret } = configForm;
    if (!tenantId || !clientId || !clientSecret) { setError("All fields are required"); return; }
    setSavingConfig(true);
    setError("");
    try {
      await saveM365Configuration(token, { tenantId: tenantId.trim(), clientId: clientId.trim(), clientSecret: clientSecret.trim() });
      const config = await getM365Configuration(token);
      setM365Config(config);
      setShowConfigModal(false);
      setConfigForm({ tenantId: "", clientId: "", clientSecret: "" });
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSavingConfig(false);
    }
  };

  const openAdConfigModal = async () => {
    setAdConfigError(""); setAdConfigSuccess(""); setShowAdModal(true);
    try {
      const data = await getWindowsAuthConfiguration(token);
      if (data) setAdConfig(data);
    } catch (e) {
      if (e.status !== 404) setAdConfigError(e.message || "Failed to load AD configuration");
    }
  };

  const handleSaveAdConfig = async () => {
    setSavingAdConfig(true); setAdConfigError(""); setAdConfigSuccess("");
    try {
      await saveWindowsAuthConfiguration(token, adConfig);
      setAdConfigSuccess("Active Directory configuration saved!");
      try { const s = await getWindowsAuthStatus(token); if (s) setAdStatus(s); } catch (_) {}
      setTimeout(() => setAdConfigSuccess(""), 3000);
    } catch (e) {
      setAdConfigError(e.message || "Failed to save configuration");
    } finally {
      setSavingAdConfig(false);
    }
  };

  const addAdGroupMapping = () => {
    const trimmed = newAdGroup.trim();
    if (!trimmed) return;
    setAdConfig((p) => ({ ...p, adGroupMappings: { ...p.adGroupMappings, [trimmed]: newAdRole } }));
    setNewAdGroup(""); setNewAdRole("User");
  };

  const removeAdGroupMapping = (groupName) => {
    setAdConfig((p) => {
      const next = { ...p.adGroupMappings };
      delete next[groupName];
      return { ...p, adGroupMappings: next };
    });
  };

  const handleAddUser = async () => {
    const org = (newOrg || userOrg || "").trim();
    const name = newName.trim();
    const email = newEmail.trim();
    if (!org) { setError("Organization is required"); return; }
    if (!name || !email) { setError("Full name and email are required"); return; }

    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_-+=";
    let password = "";
    for (let i = 0; i < 16; i++) password += chars.charAt(Math.floor(Math.random() * chars.length));

    setCreating(true); setError("");
    try {
      const created = await createOrganizationUser(token, {
        userName: email, email, password, fullName: name,
        organization: org, role: "User", phoneNumber: newPhone.trim(),
      });
      const createdId = created && (created.userId || created.id || "");
      if (newOrgAdmin && createdId) await setOrgAdmin(token, createdId, true);
      const data = await getOrganizationUsers(token);
      setItems(Array.isArray(data) ? data : []);
      setNewName(""); setNewEmail(""); setNewPhone(""); setNewOrgAdmin(false);
      setShowAddModal(false);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      {/* Action bar */}
      {isAdmin && (
        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
          <button type="button" className="chip-btn" onClick={() => { setError(""); setShowAddModal(true); }}>
            Add user
          </button>
          <button type="button" className="chip-btn" onClick={openM365Modal}>
            Import from Microsoft 365
          </button>
        </div>
      )}

      {/* Status banners */}
      {isAdmin && (!m365Config || !m365Config.isConfigured) && (
        <div className="org-users-status error">
          Microsoft 365 integration is not configured.{" "}
          <button type="button" className="link-btn" style={{ textDecoration: "underline" }}
            onClick={() => { setShowConfigModal(true); setConfigForm({ tenantId: "", clientId: "", clientSecret: "" }); setError(""); }}>
            Configure now
          </button>
        </div>
      )}
      {isAdmin && (!adStatus || !adStatus.windowsAuthEnabled) && (
        <div className="org-users-status error">
          Active Directory SSO is not enabled.{" "}
          <button type="button" className="link-btn" style={{ textDecoration: "underline" }} onClick={openAdConfigModal}>
            Configure now
          </button>
        </div>
      )}
      {isAdmin && adStatus && adStatus.windowsAuthEnabled && (
        <div className="org-users-status" style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#16a34a" }}>
          Active Directory SSO is enabled ({adStatus.configuredGroups?.length || 0} group mappings).{" "}
          <button type="button" className="link-btn" style={{ textDecoration: "underline", color: "inherit" }} onClick={openAdConfigModal}>
            Manage
          </button>
        </div>
      )}

      {!isAdmin && <div className="org-users-status error">You do not have permission to view organization users.</div>}
      {loading && <div className="org-users-status">Loading users…</div>}
      {error && <div className="org-users-status error">{error}</div>}
      {isAdmin && !loading && !error && items.length === 0 && <div className="org-users-status">No users found.</div>}

      {items.length > 0 && (
        <table className="org-users-table">
          <thead>
            <tr>
              <th>Name</th><th>Email</th><th>Phone</th><th>Roles</th>
              <th>Email confirmed</th><th>Status</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => {
              const id = u.id || u.Id;
              const name = u.fullName || u.FullName || "";
              const email = u.email || u.Email || "";
              const phone = u.phoneNumber || u.PhoneNumber || "";
              const roles = u.roles || u.Roles || [];
              const emailConfirmed = typeof u.emailConfirmed === "boolean" ? u.emailConfirmed : !!u.EmailConfirmed;
              const isLockedOut = typeof u.isLockedOut === "boolean" ? u.isLockedOut : !!u.IsLockedOut;
              const isActive = !isLockedOut;
              const roleList = Array.isArray(roles) ? roles : String(roles || "").split(",").map((s) => s.trim()).filter(Boolean);
              const isOrgAdmin = roleList.includes("OrgAdmin");
              return (
                <tr key={id}>
                  <td>{name}</td>
                  <td>{email}</td>
                  <td>{phone}</td>
                  <td>{roleList.join(", ")}</td>
                  <td>{emailConfirmed ? "Yes" : "No"}</td>
                  <td>{isActive ? "Active" : "Inactive"}</td>
                  <td>
                    <button type="button" className="org-users-action"
                      onClick={async () => {
                        try {
                          await setOrgAdmin(token, id, !isOrgAdmin);
                          setItems((prev) => prev.map((x) => {
                            const xid = x.id || x.Id;
                            if (xid !== id) return x;
                            const cur = Array.isArray(x.roles || x.Roles) ? (x.roles || x.Roles) : String(x.roles || x.Roles || "").split(",").map((s) => s.trim()).filter(Boolean);
                            const next = !isOrgAdmin ? (cur.includes("OrgAdmin") ? cur : [...cur, "OrgAdmin"]) : cur.filter((r) => r !== "OrgAdmin");
                            return { ...x, roles: next, Roles: next };
                          }));
                        } catch (e) { setError(e.message || String(e)); }
                      }}>
                      {isOrgAdmin ? "Remove Org admin" : "Make Org admin"}
                    </button>
                    <button type="button" className="org-users-action"
                      onClick={async () => {
                        try {
                          await setUserActive(token, id, !isActive);
                          setItems((prev) => prev.map((x) => {
                            const xid = x.id || x.Id;
                            if (xid !== id) return x;
                            const locked = typeof x.isLockedOut === "boolean" ? x.isLockedOut : !!x.IsLockedOut;
                            return { ...x, isLockedOut: !locked, IsLockedOut: !locked };
                          }));
                        } catch (e) { setError(e.message || String(e)); }
                      }}>
                      {isActive ? "Deactivate" : "Activate"}
                    </button>
                    <button type="button" className="org-users-action icon-only"
                      title="Export user profile for licensing"
                      aria-label="Export user profile for licensing"
                      onClick={async () => {
                        try {
                          const info = await getUserInfoById(token, id);
                          const pick = (a, b) => (info?.[a] ?? info?.[b] ?? "");
                          const bundle = {
                            version: 1,
                            type: "depository-licensing-user-profile",
                            exportedAt: new Date().toISOString(),
                            user: {
                              userId:       pick("ID", "id") || id,
                              userName:     pick("UserName", "userName") || email,
                              email:        pick("Email", "email") || email,
                              fullName:     pick("FullName", "fullName") || name,
                              doorNo:       pick("DoorNo", "doorNo"),
                              blockNo:      pick("BlockNo", "blockNo"),
                              organization: pick("Organization", "organization"),
                              roles:        roleList,
                            },
                          };
                          const slug = (bundle.user.userName || bundle.user.email || "user")
                            .replace(/[^a-zA-Z0-9_.-]/g, "_").toLowerCase();
                          const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: "application/json" });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = `licensing-profile-${slug}.json`;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          URL.revokeObjectURL(url);
                        } catch (e) { setError(e.message || String(e)); }
                      }}>
                      <span aria-hidden="true">⤓</span>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Add User Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => { if (!creating) setShowAddModal(false); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">Add user</div>
            <div className="modal-body">
              <div className="form-row"><label>Full name</label><input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Jane Doe" /></div>
              <div className="form-row"><label>Email</label><input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="user@example.com" /></div>
              <div className="form-row"><label>Phone (optional)</label><input type="tel" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} placeholder="+1 555 123 4567" /></div>
              <div className="form-row"><label>Organization</label><input type="text" value={newOrg} onChange={(e) => setNewOrg(e.target.value)} disabled={!!userOrg} /></div>
              <div className="form-row">
                <label>Org admin</label>
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
                  <input type="checkbox" checked={newOrgAdmin} onChange={(e) => setNewOrgAdmin(e.target.checked)} />
                  <span>Grant Org admin role</span>
                </label>
              </div>
              {error && <div className="error-text" role="alert">{error}</div>}
            </div>
            <div className="modal-actions">
              <button type="button" className="link-btn" disabled={creating} onClick={() => { if (!creating) setShowAddModal(false); }}>Cancel</button>
              <button type="button" className="chip-btn" disabled={creating} onClick={handleAddUser}>{creating ? "Adding…" : "Add user"}</button>
            </div>
          </div>
        </div>
      )}

      {/* M365 Import Modal */}
      {showM365Modal && (
        <div className="modal-overlay" onClick={() => { if (!activatingM365) setShowM365Modal(false); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">Import from Microsoft 365</div>
            <div className="modal-body">
              {m365Loading && <div className="org-users-status">Loading directory users</div>}
              {!m365Loading && m365Users.length === 0 && <div className="org-users-status">No users returned from Microsoft 365.</div>}
              {!m365Loading && m365Users.length > 0 && (
                <div style={{ maxHeight: 360, overflow: "auto" }}>
                  <table className="org-users-table">
                    <thead><tr><th style={{ width: 32 }} /><th>Name</th><th>Email</th><th>Department</th><th>Job title</th></tr></thead>
                    <tbody>
                      {m365Users.map((u) => {
                        const key = u.id || u.upn || u.email;
                        const email = u.email || u.upn || "";
                        const checked = !!(m365Selected && email && m365Selected[email]);
                        return (
                          <tr key={key || email}>
                            <td><input type="checkbox" checked={checked} onChange={(e) => toggleM365Selection(u, e.target.checked)} /></td>
                            <td>{u.displayName || ""}</td><td>{email}</td>
                            <td>{u.department || ""}</td><td>{u.jobTitle || ""}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {error && <div className="error-text" role="alert">{error}</div>}
            </div>
            <div className="modal-actions">
              <button type="button" className="link-btn" disabled={activatingM365} onClick={() => { if (!activatingM365) setShowM365Modal(false); }}>Cancel</button>
              <button type="button" className="chip-btn" disabled={activatingM365 || !Object.keys(m365Selected || {}).length} onClick={handleActivateFromM365}>
                {activatingM365 ? "Activating…" : "Activate selected"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* M365 Config Modal */}
      {showConfigModal && (
        <div className="modal-overlay" onClick={() => { if (!savingConfig) { setShowConfigModal(false); setConfigForm({ tenantId: "", clientId: "", clientSecret: "" }); } }}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">Configure Microsoft 365 Integration</div>
            <div className="modal-body">
              <p style={{ marginBottom: 16, color: "#4b5563", fontSize: 14 }}>Configure Azure AD (Entra ID) settings to enable user import from Microsoft 365.</p>
              <div className="form-row"><label>Tenant ID</label><input type="text" value={configForm.tenantId} onChange={(e) => setConfigForm((p) => ({ ...p, tenantId: e.target.value }))} placeholder="00000000-0000-0000-0000-000000000000" /></div>
              <div className="form-row"><label>Client ID (Application ID)</label><input type="text" value={configForm.clientId} onChange={(e) => setConfigForm((p) => ({ ...p, clientId: e.target.value }))} placeholder="00000000-0000-0000-0000-000000000000" /></div>
              <div className="form-row"><label>Client Secret</label><input type="password" value={configForm.clientSecret} onChange={(e) => setConfigForm((p) => ({ ...p, clientSecret: e.target.value }))} placeholder="Enter client secret" /></div>
              {error && <div className="error-text" role="alert">{error}</div>}
            </div>
            <div className="modal-actions">
              <button type="button" className="link-btn" disabled={savingConfig} onClick={() => { if (!savingConfig) { setShowConfigModal(false); setConfigForm({ tenantId: "", clientId: "", clientSecret: "" }); } }}>Cancel</button>
              <button type="button" className="chip-btn" disabled={savingConfig} onClick={handleSaveM365Config}>{savingConfig ? "Saving…" : "Save configuration"}</button>
            </div>
          </div>
        </div>
      )}

      {/* AD Config Modal */}
      {showAdModal && (
        <div className="modal-overlay" onClick={() => { if (!savingAdConfig) setShowAdModal(false); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640, width: "95vw" }}>
            <div className="modal-title">Active Directory Configuration</div>
            <div className="modal-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>
              <p style={{ marginBottom: 16, color: "#4b5563", fontSize: 14 }}>Configure Windows Authentication (Kerberos/NTLM) and AD group-to-role mappings for enterprise SSO.</p>
              {adConfigError && <div className="error-text" role="alert" style={{ marginBottom: 12 }}>{adConfigError}</div>}
              {adConfigSuccess && <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#16a34a", padding: "8px 12px", borderRadius: 8, marginBottom: 12, fontSize: 13 }}>{adConfigSuccess}</div>}
              {[
                { field: "enabled",              label: "Windows Auth Enabled",    desc: "Enable Kerberos/NTLM single sign-on" },
                { field: "requireGroupMembership",label: "Require Group Membership",desc: "Users must belong to a mapped AD group" },
                { field: "autoProvisionUsers",   label: "Auto-Provision Users",    desc: "Create accounts for new domain users on first login" },
                { field: "syncRolesFromAd",      label: "Sync Roles from AD",      desc: "Update application roles from AD groups on each login" },
              ].map(({ field, label, desc }) => (
                <div key={field} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #f3f4f6" }}>
                  <div>
                    <strong style={{ fontSize: 14 }}>{label}</strong>
                    <p style={{ color: "#6b7280", fontSize: 12, margin: "2px 0 0" }}>{desc}</p>
                  </div>
                  <Toggle value={adConfig[field]} onChange={(v) => setAdConfig((p) => ({ ...p, [field]: v }))} />
                </div>
              ))}
              <div style={{ marginTop: 16 }}>
                <div className="form-row">
                  <label>Default Role</label>
                  <select value={adConfig.defaultRole || "User"} onChange={(e) => setAdConfig((p) => ({ ...p, defaultRole: e.target.value }))} style={{ width: "100%" }}>
                    {AD_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div className="form-row"><label>Default Organization</label><input type="text" value={adConfig.defaultOrganization || ""} onChange={(e) => setAdConfig((p) => ({ ...p, defaultOrganization: e.target.value }))} placeholder="e.g. ACME Bank" /></div>
                <div className="form-row"><label>Default Email Domain</label><input type="text" value={adConfig.defaultEmailDomain || ""} onChange={(e) => setAdConfig((p) => ({ ...p, defaultEmailDomain: e.target.value }))} placeholder="e.g. bank.local" /></div>
              </div>
              <h4 style={{ marginTop: 20, marginBottom: 8, fontSize: 14, fontWeight: 600 }}>AD Group → Role Mappings</h4>
              {Object.keys(adConfig.adGroupMappings || {}).length > 0 && (
                <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 12, fontSize: 13 }}>
                  <thead><tr>
                    <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "2px solid #e5e7eb", fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase" }}>Group</th>
                    <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "2px solid #e5e7eb", fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase" }}>Role</th>
                    <th style={{ width: 60, padding: "6px 8px", borderBottom: "2px solid #e5e7eb" }} />
                  </tr></thead>
                  <tbody>
                    {Object.entries(adConfig.adGroupMappings).map(([group, role]) => (
                      <tr key={group}>
                        <td style={{ padding: "6px 8px", borderBottom: "1px solid #f3f4f6" }}><code style={{ background: "#f1f5f9", padding: "1px 6px", borderRadius: 4, fontSize: 12 }}>{group}</code></td>
                        <td style={{ padding: "6px 8px", borderBottom: "1px solid #f3f4f6" }}><span style={{ background: adRoleColor(role), color: "#fff", padding: "1px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{role}</span></td>
                        <td style={{ padding: "6px 8px", borderBottom: "1px solid #f3f4f6" }}><button type="button" onClick={() => removeAdGroupMapping(group)} style={{ background: "none", border: "1px solid #fecaca", color: "#dc2626", borderRadius: 4, padding: "2px 8px", cursor: "pointer", fontSize: 11 }}>✕</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input type="text" value={newAdGroup} onChange={(e) => setNewAdGroup(e.target.value)} placeholder="AD group name" style={{ flex: 1 }} onKeyDown={(e) => { if (e.key === "Enter") addAdGroupMapping(); }} />
                <select value={newAdRole} onChange={(e) => setNewAdRole(e.target.value)} style={{ width: 130 }}>{AD_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}</select>
                <button type="button" className="chip-btn" onClick={addAdGroupMapping} disabled={!newAdGroup.trim()} style={{ whiteSpace: "nowrap", fontSize: 12 }}>Add</button>
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="link-btn" disabled={savingAdConfig} onClick={() => { if (!savingAdConfig) setShowAdModal(false); }}>Cancel</button>
              <button type="button" className="chip-btn" disabled={savingAdConfig} onClick={handleSaveAdConfig}>{savingAdConfig ? "Saving…" : "Save configuration"}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Combined page
// ─────────────────────────────────────────────────────────────────────────────
export default function UserGroupAdmin() {
  const { token, user } = useAuth();
  const isAdmin = useIsAdmin();
  const userOrg = (user && (user.organization || user.Organization)) || "";
  const [tab, setTab] = useState("users");

  const tabStyle = (active) => ({
    padding: "8px 22px",
    fontWeight: 600,
    fontSize: 14,
    borderRadius: "8px 8px 0 0",
    border: "none",
    cursor: "pointer",
    background: active ? "#fff" : "transparent",
    color: active ? "#6d28d9" : "#6b7280",
    borderBottom: active ? "2px solid #7c3aed" : "2px solid transparent",
    transition: "color 0.15s",
  });

  return (
    <div className="app-root">
      <div className="nav-strip"><NavBar /></div>
      <HeaderBar />
      <div className="page-container">
        <div className="org-users-page">
          <Hero
            title="Users & Groups"
            subtitle="Manage organization members and define group permissions for database access."
            art={<UsersArt />}
          />

          {/* Tab bar */}
          <div style={{ display: "flex", gap: 2, marginTop: 20, marginBottom: 0, borderBottom: "1px solid #e5e7eb" }}>
            <button type="button" style={tabStyle(tab === "users")} onClick={() => setTab("users")}>
              Users
            </button>
            <button type="button" style={tabStyle(tab === "groups")} onClick={() => setTab("groups")}>
              Groups &amp; Rights
            </button>
          </div>

          {/* Tab content */}
          <div style={{ paddingTop: 20 }}>
            {tab === "users" && <UsersTab token={token} isAdmin={isAdmin} userOrg={userOrg} />}
            {tab === "groups" && <GroupsTab token={token} isAdmin={isAdmin} />}
          </div>
        </div>
      </div>
    </div>
  );
}
